import ExcelJS from 'exceljs';

const DEFAULT_PAGE_SIZE = 200;
const MAX_RETRIES = 2;
const FORMULA_PREFIX = /^[=+\-@]/;

export class TaxExportError extends Error {
  constructor(message, { code = 'TAX_EXPORT_ERROR', page = null, status = null, cause = null } = {}) {
    super(message, cause ? { cause } : undefined);
    this.name = 'TaxExportError';
    this.code = code;
    this.page = page;
    this.status = status;
  }
}

function wait(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function errorStatus(error) {
  return error?.httpStatus ?? error?.response?.status ?? error?.status ?? null;
}

function isTransient(error) {
  const status = errorStatus(error);
  return status == null || status === 408 || status === 429 || status >= 500;
}

async function fetchWithRetry(fetchPage, request) {
  let lastError;
  for (let attempt = 0; attempt <= MAX_RETRIES; attempt += 1) {
    try {
      return await fetchPage(request);
    } catch (error) {
      lastError = error;
      if (!isTransient(error) || attempt === MAX_RETRIES) break;
      console.warn('Tax Excel export request failed; retrying.', {
        page: request.page,
        attempt: attempt + 1,
        status: errorStatus(error),
        message: error?.message || String(error),
      });
      await wait(250 * (attempt + 1));
    }
  }

  const status = errorStatus(lastError);
  throw new TaxExportError(
    `Unable to export page ${request.page}${status ? ` (HTTP ${status})` : ''}: ${lastError?.message || 'Request failed'}`,
    { code: 'EXPORT_REQUEST_FAILED', page: request.page, status, cause: lastError },
  );
}

/**
 * Read a stable filtered result through the existing paginated list API.
 * It deliberately refuses to return partial data when paging invariants change.
 */
export async function collectExportRows({
  fetchPage,
  filters = {},
  rowKey,
  pageSize = DEFAULT_PAGE_SIZE,
}) {
  if (typeof fetchPage !== 'function') {
    throw new TypeError('collectExportRows requires fetchPage.');
  }
  if (typeof rowKey !== 'function') {
    throw new TypeError('collectExportRows requires rowKey.');
  }

  const size = Number(pageSize);
  if (!Number.isInteger(size) || size < 1) {
    throw new RangeError('collectExportRows pageSize must be a positive integer.');
  }

  const rows = [];
  const keys = new Set();
  let expectedTotal = null;
  let page = 1;

  while (expectedTotal === null || rows.length < expectedTotal) {
    const result = await fetchWithRetry(fetchPage, { ...filters, page, pageSize: size });
    const items = Array.isArray(result?.items) ? result.items : null;
    const total = Number(result?.totalCount);
    const returnedPage = Number(result?.page ?? result?.pageNumber ?? page);

    if (!items || !Number.isInteger(total) || total < 0) {
      throw new TaxExportError(`Export page ${page} returned an invalid result.`, {
        code: 'INVALID_EXPORT_PAGE',
        page,
      });
    }
    if (returnedPage !== page) {
      throw new TaxExportError(`Export requested page ${page}, but the server returned page ${returnedPage}.`, {
        code: 'WRONG_EXPORT_PAGE',
        page,
      });
    }
    if (expectedTotal === null) expectedTotal = total;
    if (total !== expectedTotal) {
      throw new TaxExportError(
        `The filtered result count changed during export (${expectedTotal} to ${total}). Please retry.`,
        { code: 'EXPORT_TOTAL_CHANGED', page },
      );
    }
    if (items.length === 0 && rows.length < expectedTotal) {
      throw new TaxExportError(`Export page ${page} was empty before all ${expectedTotal} rows were received.`, {
        code: 'EXPORT_ENDED_EARLY',
        page,
      });
    }

    for (const item of items) {
      const key = String(rowKey(item) ?? '');
      if (!key) {
        throw new TaxExportError(`Export page ${page} contains a row without an identity.`, {
          code: 'EXPORT_ROW_WITHOUT_IDENTITY',
          page,
        });
      }
      if (keys.has(key)) {
        throw new TaxExportError(`Export page ${page} contains duplicate row ${key}. Please retry.`, {
          code: 'DUPLICATE_EXPORT_ROW',
          page,
        });
      }
      keys.add(key);
      rows.push(item);
    }

    if (rows.length > expectedTotal) {
      throw new TaxExportError(`Export received more rows than the filtered total (${expectedTotal}). Please retry.`, {
        code: 'EXPORT_TOTAL_EXCEEDED',
        page,
      });
    }
    page += 1;
  }

  return rows;
}

function safeCellValue(value) {
  if (value === null || value === undefined) return '';
  if (typeof value !== 'string') return value;
  return FORMULA_PREFIX.test(value) ? `'${value}` : value;
}

export async function buildTaxWorkbook({ sheetName, columns, rows }) {
  if (!Array.isArray(columns) || columns.length === 0) {
    throw new TypeError('buildTaxWorkbook requires at least one column.');
  }

  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'Chat Framework';
  workbook.created = new Date();

  const worksheet = workbook.addWorksheet(String(sheetName || 'Export').slice(0, 31));
  worksheet.columns = columns.map((column) => ({
    header: column.header,
    key: column.key,
    width: column.width || 15,
  }));
  worksheet.views = [{ state: 'frozen', ySplit: 1 }];

  for (const source of rows || []) {
    const row = {};
    for (const column of columns) row[column.key] = safeCellValue(source?.[column.key]);
    worksheet.addRow(row);
  }

  const header = worksheet.getRow(1);
  header.height = 24;
  header.eachCell((cell) => {
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1565C0' } };
    cell.alignment = { vertical: 'middle', horizontal: 'center' };
  });

  worksheet.eachRow((row, rowNumber) => {
    if (rowNumber === 1) return;
    row.alignment = { vertical: 'top', wrapText: true };
    if (rowNumber % 2 === 0) {
      row.eachCell((cell) => {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF5F8FC' } };
      });
    }
  });

  worksheet.autoFilter = {
    from: 'A1',
    to: worksheet.getCell(Math.max(worksheet.rowCount, 1), columns.length).address,
  };

  return workbook.xlsx.writeBuffer();
}

export async function downloadTaxWorkbook({ filename, sheetName, columns, rows }) {
  if (typeof document === 'undefined' || typeof window === 'undefined') {
    throw new TaxExportError('Excel download requires a browser.', { code: 'BROWSER_REQUIRED' });
  }

  const buffer = await buildTaxWorkbook({ sheetName, columns, rows });
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  try {
    link.href = url;
    link.download = filename;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
  } finally {
    link.remove();
    window.URL.revokeObjectURL(url);
  }
}

export function exportTimestamp(date = new Date()) {
  const pad = (value) => String(value).padStart(2, '0');
  return `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}_${pad(date.getHours())}${pad(date.getMinutes())}${pad(date.getSeconds())}`;
}
